import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Snackbar } from 'Components';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { useInviteLink } from 'hooks/useInviteLink';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { Pathname } from 'Routes';
import { ViewerPage } from '../components/page';
import { ViewersTable } from '../components/viewers-table';
import { ViewersClassic } from './ViewersClassic';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

const ViewersNew = () => {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const { isViewerListUpdated, setViewerListUpdate } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { copyInviteLink } = useInviteLink();

  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [linkCopied, setLinkCopied] = useState(false);
  const [inGrantFlow, setInGrantFlow] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all' | 'active' | 'pending'

  // Read straight from the SWR cache (revalidateOnMount keeps it fresh) so
  // returning to this tab paints the cached lists instantly instead of
  // resetting to empty and reloading. `mutate` still refetches after mutations.
  const { data: viewersResp, mutate: fetchViewerList } = useSWR([endpoints.getViewersList], {
    fetcher: (url) => dataFetcher(url, {}),
    revalidateOnMount: true,
  });
  const { data: pendingResp, mutate: fetchPendingList } = useSWR([endpoints.getPendingViewersList], {
    fetcher: (url) => dataFetcher(url, {}),
    revalidateOnMount: true,
  });
  const viewers = viewersResp?.success && viewersResp.data ? viewersResp.data : [];
  const pendingViewersList = pendingResp?.success && pendingResp.data ? pendingResp.data : [];

  const notify = (ok, message) => setSnackbar({
    isVisible: true,
    type: ok ? 'success' : 'error',
    message: ok ? message : 'Oops! Something went wrong',
  });

  const handleCopyLink = async () => {
    const copied = await copyInviteLink();
    if (copied) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1000);
      setSnackbar({ isVisible: true, message: 'Invite link copied to clipboard!' });
    } else {
      setSnackbar({ isVisible: true, type: 'error', message: 'Could not copy the link. Please copy it manually.' });
    }
  };

  const handleCancelInvite = (email) => {
    dataFetcher(endpoints.cancelInvitation, { email })
      .then((res) => { if (res?.success) fetchPendingList(); notify(res?.success, 'Invitation cancelled'); })
      .catch(() => notify(false));
  };

  const handleDeleteViewer = (id, name) => {
    dataFetcher(endpoints.deleteMyViewer, { id })
      .then((res) => { if (res?.success) fetchViewerList(); notify(res?.success, `${name || 'Viewer'} removed`); })
      .catch(() => notify(false));
  };

  // Refetch when a viewer changes elsewhere in the app.
  useEffect(() => {
    if (isViewerListUpdated) {
      fetchViewerList();
      fetchPendingList();
      setViewerListUpdate(false);
    }
  }, [isViewerListUpdated]);

  // Refresh the pending list once the grant-access flow (?grantAccess) closes.
  useEffect(() => {
    if (location.search?.includes(Pathname.grantAccess)) {
      setInGrantFlow(true);
    } else if (inGrantFlow) {
      setInGrantFlow(false);
      fetchPendingList();
    }
  }, [location.search]);

  const rows = [
    ...(filterType !== 'pending' ? (viewers || []).map((v) => ({ ...v, status: 'active' })) : []),
    ...(filterType !== 'active' ? (pendingViewersList || []).map((v) => ({ ...v, status: 'pending' })) : []),
  ];

  return (
    <>
      <ViewerPage title="Viewers" link={Pathname.grantAccess} hideHeaderActions>
        <ViewersTable
          rows={rows}
          filter={filterType}
          onFilterChange={setFilterType}
          activeCount={viewers?.length || 0}
          pendingCount={pendingViewersList?.length || 0}
          copied={linkCopied}
          onCopy={handleCopyLink}
          onInvite={() => navigate(Pathname.grantAccess)}
          onCancelInvite={handleCancelInvite}
          onRemove={handleDeleteViewer}
          emptyText={filterType === 'pending' ? 'No pending requests' : 'No active viewers yet'}
        />
      </ViewerPage>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => setSnackbar(defaultSnackbar)}
        type={snackbar?.type}
      />
    </>
  );
};

export const Viewers = (props) => (
  isNewUIEnabled() ? <ViewersNew {...props} /> : <ViewersClassic {...props} />
);
